import React, { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { getOnchain } from '../api';
import { useIsMobile } from '../hooks/useIsMobile';

const STATUS_META = {
  spike:  { color: '#dc2626', emoji: '📈', key: 'onchain.statusSpike' },
  drop:   { color: '#2563eb', emoji: '📉', key: 'onchain.statusDrop' },
  normal: { color: '#64748b', emoji: '•', key: 'onchain.statusNormal' },
};

const SOURCE_LABEL = {
  binance: 'Binance', okx: 'OKX', bybit: 'Bybit', mexc: 'MEXC', dexscreener: 'DexScreener',
};

function fmtUsd(n) {
  if (n == null || !Number.isFinite(n)) return '—';
  if (n >= 1e9) return `$${(n / 1e9).toFixed(2)}B`;
  if (n >= 1e6) return `$${(n / 1e6).toFixed(2)}M`;
  if (n >= 1e3) return `$${(n / 1e3).toFixed(1)}K`;
  return `$${n.toFixed(0)}`;
}

function fmtPrice(n) {
  if (n == null || !Number.isFinite(n)) return '—';
  if (n >= 1) return `$${n.toLocaleString('en-US', { maximumFractionDigits: 2 })}`;
  return `$${n.toPrecision(3)}`;
}

function devPct(ratio) {
  if (ratio == null || !Number.isFinite(ratio)) return '—';
  const pct = (ratio - 1) * 100;
  return `${pct >= 0 ? '+' : ''}${pct.toFixed(0)}%`;
}

export default function OnchainPage() {
  const { t } = useTranslation();
  const isMobile = useIsMobile();
  const [tokens, setTokens] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    try { const { tokens } = await getOnchain(); setTokens(tokens || []); }
    catch { /* silent */ }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); const timer = setInterval(load, 60000); return () => clearInterval(timer); }, [load]);

  const pageStyle = isMobile ? { ...s.page, padding: '16px 12px' } : s.page;

  return (
    <div style={pageStyle}>
      <div style={s.header}>
        <div>
          <h2 style={s.title}>⛓️ {t('onchain.title')}</h2>
          <p style={s.subtitle}>{t('onchain.subtitle', { count: tokens.length })}</p>
        </div>
      </div>

      {loading ? (
        <div style={s.loading}>{t('onchain.loading')}</div>
      ) : tokens.length === 0 ? (
        <div style={s.empty}>
          <div style={{ fontSize: 48, marginBottom: 12 }}>⛓️</div>
          <div style={{ fontSize: 16, fontWeight: 700, color: 'var(--text)', marginBottom: 8 }}>
            {t('onchain.emptyTitle')}
          </div>
          <div style={{ fontSize: 13, color: 'var(--text-muted)', maxWidth: 460, textAlign: 'center' }}>
            {t('onchain.emptyDesc')}
          </div>
        </div>
      ) : isMobile ? (
        <div style={s.cards}>
          {tokens.map(tok => <TokenCard key={tok.slug} tok={tok} t={t} />)}
        </div>
      ) : (
        <div style={s.tableWrap}>
          <div style={s.thead}>
            <span style={{ ...s.th, flex: 2 }}>{t('onchain.colToken')}</span>
            <span style={s.th}>{t('onchain.colSource')}</span>
            <span style={s.th}>{t('onchain.colPrice')}</span>
            <span style={s.th}>{t('onchain.colLatestVolume')}</span>
            <span style={s.th} title={t('onchain.baselineTooltip')}>{t('onchain.colBaseline')} ⓘ</span>
            <span style={s.th}>{t('onchain.colDeviation')}</span>
            <span style={{ ...s.th, flex: 1.3 }}>{t('onchain.colLive')}</span>
            <span style={{ ...s.th, flex: 1.4 }}>{t('onchain.colBuySell')}</span>
            <span style={{ ...s.th, flex: 1.2 }}>{t('onchain.colStatus')}</span>
          </div>
          {tokens.map(tok => {
            const st = STATUS_META[tok.statusKey] || STATUS_META.normal;
            return (
              <div key={tok.slug} style={s.row}>
                <span style={{ ...s.td, flex: 2, fontWeight: 700, color: 'var(--text)' }}>
                  {tok.slug} {tok.ticker ? <span style={s.ticker}>{tok.ticker}</span> : null}
                </span>
                <span style={s.td}>{SOURCE_LABEL[tok.source] || tok.source}{tok.estimated ? '*' : ''}</span>
                <span style={s.td}>{fmtPrice(tok.price)}</span>
                <span style={{ ...s.td, fontWeight: 600 }}>{fmtUsd(tok.latestVolume)}</span>
                <span style={s.td}>
                  {fmtUsd(tok.avgVolume)}
                  {tok.baselineNote && <span style={s.infoIcon} title={tok.baselineNote}>ⓘ</span>}
                </span>
                <span style={{ ...s.td, color: st.color, fontWeight: 600 }}>{devPct(tok.ratio)}</span>
                <span style={{ ...s.td, flex: 1.3 }}><Live tok={tok} t={t} /></span>
                <span style={{ ...s.td, flex: 1.4 }}><BuySell buy={tok.buyVolume} sell={tok.sellVolume} t={t} /></span>
                <span style={{ ...s.td, flex: 1.2 }}>
                  <span style={{ ...s.badge, color: st.color, borderColor: st.color + '55', background: st.color + '14' }}>
                    {st.emoji} {t(st.key)}
                  </span>
                </span>
              </div>
            );
          })}
          <div style={s.explain}>
            <strong style={{ color: 'var(--text-dim)' }}>{t('onchain.explainTitle')}</strong> {t('onchain.explainBody')}
          </div>
          <p style={s.note}>{t('onchain.dexscreenerNote')}</p>
        </div>
      )}
    </div>
  );
}

function Live({ tok, t }) {
  if (tok.liveVolume == null) return <span style={{ color: 'var(--text-dim)' }}>—</span>;
  const spike = tok.liveSpike;
  const color = spike ? '#dc2626' : 'var(--text-dim)';
  return (
    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 5, color, fontWeight: spike ? 700 : 500, minWidth: 0 }}>
      {spike && <span style={s.liveDot} />}
      {fmtUsd(tok.liveVolume)} <span style={{ fontSize: 10 }}>({devPct(tok.liveRatio)})</span>
    </span>
  );
}

function BuySell({ buy, sell, t }) {
  if (buy == null && sell == null) return <span style={{ color: 'var(--text-dim)' }}>—</span>;
  const total = (buy || 0) + (sell || 0);
  const buyPct = total ? Math.round((buy / total) * 100) : 50;
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 3, minWidth: 0 }}>
      <div style={{ display: 'flex', height: 5, borderRadius: 3, overflow: 'hidden', background: 'var(--border)' }}>
        <div style={{ width: `${buyPct}%`, background: '#10b981' }} />
        <div style={{ width: `${100 - buyPct}%`, background: '#ef4444' }} />
      </div>
      <span style={{ fontSize: 10, color: 'var(--text-dim)' }}>{t('onchain.buySellPct', { buy: buyPct, sell: 100 - buyPct })}</span>
    </div>
  );
}

function TokenCard({ tok, t }) {
  const st = STATUS_META[tok.statusKey] || STATUS_META.normal;
  return (
    <div style={s.card}>
      <div style={s.cardTop}>
        <span style={s.cardName}>{tok.slug} {tok.ticker ? <span style={s.ticker}>{tok.ticker}</span> : null}</span>
        <span style={{ ...s.badge, color: st.color, borderColor: st.color + '55', background: st.color + '14' }}>
          {st.emoji} {t(st.key)}
        </span>
      </div>
      <div style={s.cardGrid}>
        <Cell label={t('onchain.colPrice')} value={fmtPrice(tok.price)} />
        <Cell label={t('onchain.colLatestVolume')} value={fmtUsd(tok.latestVolume)} />
        <Cell label={t('onchain.colBaseline')} value={fmtUsd(tok.avgVolume)} />
        <Cell label={t('onchain.colDeviation')} value={devPct(tok.ratio)} color={st.color} />
      </div>
      {tok.baselineNote && <div style={s.noteBox}>💡 {tok.baselineNote}</div>}
      <div style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 12, color: tok.liveSpike ? '#dc2626' : 'var(--text-muted)', fontWeight: tok.liveSpike ? 700 : 500 }}>
        {tok.liveSpike && <span style={s.liveDot} />}
        {t('onchain.colLive')}: <Live tok={tok} t={t} />
      </div>
      <BuySell buy={tok.buyVolume} sell={tok.sellVolume} t={t} />
      <span style={s.cardMeta}>{t('onchain.sourceLabel')}: {SOURCE_LABEL[tok.source] || tok.source}{tok.estimated ? ` (${t('onchain.estimated')})` : ''}</span>
    </div>
  );
}

function Cell({ label, value, color }) {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 2, minWidth: 0 }}>
      <span style={{ fontSize: 9, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: 0.5 }}>{label}</span>
      <span style={{ fontSize: 13, fontWeight: 700, color: color || 'var(--text)', wordBreak: 'break-word' }}>{value}</span>
    </div>
  );
}

const s = {
  page: { padding: '24px 32px', maxWidth: 1200, margin: '0 auto' },
  header: { marginBottom: 20 },
  title: { margin: '0 0 4px', fontSize: 20, fontWeight: 800, color: 'var(--text)' },
  subtitle: { margin: 0, fontSize: 13, color: 'var(--text-muted)' },
  loading: { color: 'var(--text-muted)', textAlign: 'center', padding: 60, fontSize: 14 },
  empty: { display: 'flex', flexDirection: 'column', alignItems: 'center', padding: '70px 20px' },

  tableWrap: { background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 14, padding: '8px 14px 14px', overflow: 'hidden' },
  thead: { display: 'flex', gap: 12, padding: '10px 8px', borderBottom: '1px solid var(--border)', marginBottom: 4 },
  th: { flex: 1, fontSize: 10, fontWeight: 700, color: 'var(--text-muted)', letterSpacing: 0.7, textTransform: 'uppercase', minWidth: 0 },
  row: { display: 'flex', gap: 12, alignItems: 'center', padding: '11px 8px', borderBottom: '1px solid var(--border)', minWidth: 0 },
  td: { flex: 1, fontSize: 12, color: 'var(--text-dim)', minWidth: 0, wordBreak: 'break-word', overflowWrap: 'break-word' },
  ticker: { fontSize: 10, color: 'var(--text-muted)', fontWeight: 600 },
  liveDot: { width: 6, height: 6, borderRadius: '50%', background: '#dc2626', display: 'inline-block', flexShrink: 0, animation: 'pulse 1.4s ease-in-out infinite' },
  infoIcon: { marginLeft: 4, fontSize: 11, color: 'var(--accent)', cursor: 'help' },
  noteBox: { fontSize: 11, color: 'var(--text-muted)', lineHeight: 1.5, background: 'var(--surface2)', border: '1px solid var(--border)', borderRadius: 8, padding: '8px 10px', wordBreak: 'break-word', overflowWrap: 'break-word' },
  badge: { fontSize: 10, fontWeight: 700, border: '1px solid', borderRadius: 8, padding: '3px 9px', whiteSpace: 'nowrap', display: 'inline-block' },
  note: { fontSize: 10, color: 'var(--text-dim)', margin: '10px 0 0' },
  explain: { fontSize: 11, color: 'var(--text-muted)', lineHeight: 1.6, marginTop: 12, padding: '10px 12px', background: 'var(--surface2)', border: '1px solid var(--border)', borderRadius: 8 },

  cards: { display: 'flex', flexDirection: 'column', gap: 10 },
  card: { background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 12, padding: 14, display: 'flex', flexDirection: 'column', gap: 10, minWidth: 0 },
  cardTop: { display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8, flexWrap: 'wrap' },
  cardName: { fontSize: 14, fontWeight: 700, color: 'var(--text)', wordBreak: 'break-word' },
  cardGrid: { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10, minWidth: 0 },
  cardMeta: { fontSize: 10, color: 'var(--text-dim)' },
};
